export interface StackGuide {
  slug: string;
  name: string;
  frameworks: string;
  productPath: string;
  dependencyFile: string;
  buildCommand: string;
  runCommand: string;
  portNote: string;
  productionNotes: string[];

  // New SEO Fields
  primaryKeyword: string;
  secondaryKeywords: string[];
  seoTitle: string;
  seoDescription: string;
  h1Title: string;
  keywords: string;
  relatedLinks: Array<{ label: string; to: string }>;
}

export const STACK_GUIDES: StackGuide[] = [
  {
    slug: "nodejs",
    name: "Node.js",
    frameworks: "Express، NestJS و Fastify",
    productPath: "/products/nodejs",
    dependencyFile: "package.json و lockfile مربوط به npm، yarn یا pnpm",
    buildCommand: "npm ci && npm run build",
    runCommand: "npm start",
    portNote: "سرور HTTP باید روی process.env.PORT و آدرس 0.0.0.0 گوش دهد.",
    productionNotes: [
      "نسخه Node را در engines فایل package.json ثابت کنید.",
      "برای worker یک سرویس جدا با فرمان اجرای مستقل بسازید.",
      "secretها را در variables قرار دهید، نه فایل .env داخل Git.",
    ],
    primaryKeyword: "دیپلوی nodejs",
    secondaryKeywords: ["دیپلوی پروژه node js", "آموزش دیپلوی nodejs", "دیپلوی express", "دیپلوی nestjs"],
    seoTitle: "دیپلوی nodejs با Express و NestJS | گردو کلاد",
    seoDescription: "دیپلوی nodejs روی سکوی ابری گردو برای Express، NestJS و Fastify؛ از Git یا CLI، با دستور ساخت و اجرا، متغیرها و دامنه. پروژه را در چند دقیقه منتشر کنید.",
    h1Title: "دیپلوی nodejs؛ Express، NestJS و Fastify روی گردو",
    keywords: "دیپلوی nodejs, دیپلوی پروژه node js, آموزش دیپلوی nodejs, دیپلوی express, دیپلوی nestjs, هاست ابری nodejs",
    relatedLinks: [
      { label: "هاست ابری nodejs", to: "/products/nodejs" },
      { label: "آموزش دیپلوی", to: "/guides" },
      { label: "سکوی ابری گردو", to: "/products/paas" },
    ],
  },
  {
    slug: "laravel",
    name: "Laravel و PHP",
    frameworks: "Laravel، PHP و Artisan",
    productPath: "/products/laravel",
    dependencyFile: "composer.json و composer.lock",
    buildCommand: "composer install --no-dev --optimize-autoloader && php artisan config:cache",
    runCommand: "php artisan serve --host=0.0.0.0 --port=$PORT",
    portNote: "APP_KEY، APP_ENV، APP_URL و اطلاعات دیتابیس را به‌صورت variable ثبت کنید.",
    productionNotes: [
      "migration را آگاهانه و پس از backup اجرا کنید.",
      "queue worker و scheduler را به‌صورت سرویس یا cron جدا اجرا کنید.",
      "برای فایل‌های upload از دیسک ماندگار یا MinIO استفاده کنید.",
    ],
    primaryKeyword: "دیپلوی لاراول",
    secondaryKeywords: ["آپلود پروژه لاراول روی هاست", "دیپلوی پروژه لاراول", "آموزش دیپلوی لاراول", "دیپلوی php"],
    seoTitle: "دیپلوی لاراول و PHP روی سکوی ابری | گردو کلاد",
    seoDescription: "دیپلوی لاراول و PHP روی سکوی ابری گردو از Git یا CLI؛ دستور ساخت، Artisan، متغیرها، دیتابیس MySQL و دامنه. پروژه‌ی Laravel را بدون سی‌پنل منتشر کنید.",
    h1Title: "دیپلوی لاراول و PHP روی گردو",
    keywords: "دیپلوی لاراول, آپلود پروژه لاراول روی هاست, دیپلوی پروژه لاراول, آموزش دیپلوی لاراول, دیپلوی php, خرید هاست لاراول",
    relatedLinks: [
      { label: "خرید هاست لاراول", to: "/products/laravel" },
      { label: "خرید دیتابیس mysql", to: "/marketplace/apps/mysql" },
      { label: "آموزش دیپلوی", to: "/guides" },
    ],
  },
  {
    slug: "go",
    name: "Go",
    frameworks: "Gin، Echo، Fiber و net/http",
    productPath: "/products/go",
    dependencyFile: "go.mod و go.sum",
    buildCommand: "go build -o app .",
    runCommand: "./app",
    portNote: "برنامه باید PORT را از محیط بخواند و روی 0.0.0.0 bind شود.",
    productionNotes: [
      "CGO را فقط وقتی dependency بومی دارید فعال کنید.",
      "باینری را با timeout و shutdown آرام برای SIGTERM آماده کنید.",
      "endpoint سلامت سبک و بدون وابستگی سنگین بسازید.",
    ],
    primaryKeyword: "دیپلوی golang",
    secondaryKeywords: ["دیپلوی پروژه go", "دیپلوی گو", "آموزش دیپلوی golang"],
    seoTitle: "دیپلوی golang؛ Gin، Echo و Fiber | گردو کلاد",
    seoDescription: "دیپلوی golang روی سکوی ابری گردو برای Gin، Echo، Fiber و net/http؛ از Git یا CLI با دستور ساخت و اجرا، متغیرها و دامنه. سرویس Go خود را منتشر کنید.",
    h1Title: "دیپلوی golang روی گردو",
    keywords: "دیپلوی golang, دیپلوی پروژه go, دیپلوی گو, آموزش دیپلوی golang, هاست golang",
    relatedLinks: [
      { label: "هاست golang", to: "/products/go" },
      { label: "آموزش دیپلوی", to: "/guides" },
      { label: "سکوی ابری گردو", to: "/products/paas" },
    ],
  },
  {
    slug: "ruby",
    name: "Ruby",
    frameworks: "Rails، Sinatra و Rack",
    productPath: "/products/ruby",
    dependencyFile: "Gemfile و Gemfile.lock",
    buildCommand: "bundle install && bundle exec rails assets:precompile",
    runCommand: "bundle exec puma -C config/puma.rb",
    portNote: "Puma باید روی tcp://0.0.0.0:$PORT گوش دهد.",
    productionNotes: [
      "RAILS_MASTER_KEY و DATABASE_URL را secret کنید.",
      "migration را پیش از جابه‌جایی release بررسی کنید.",
      "Sidekiq یا workerهای دیگر را در سرویس جدا اجرا کنید.",
    ],
    primaryKeyword: "دیپلوی ruby",
    secondaryKeywords: ["دیپلوی rails", "دیپلوی ruby on rails", "دیپلوی روبی"],
    seoTitle: "دیپلوی ruby؛ Rails و Sinatra روی ابر | گردو کلاد",
    seoDescription: "دیپلوی ruby روی سکوی ابری گردو برای Rails، Sinatra و Rack؛ از Git یا CLI با دستور ساخت و اجرا، متغیرها و دامنه. پروژه‌ی Ruby را در چند دقیقه منتشر کنید.",
    h1Title: "دیپلوی ruby؛ Rails و Sinatra روی گردو",
    keywords: "دیپلوی ruby, دیپلوی rails, دیپلوی ruby on rails, دیپلوی روبی, هاست روبی",
    relatedLinks: [
      { label: "هاست روبی", to: "/products/ruby" },
      { label: "آموزش دیپلوی", to: "/guides" },
      { label: "سکوی ابری گردو", to: "/products/paas" },
    ],
  },
  {
    slug: "rust",
    name: "Rust",
    frameworks: "Axum، Actix Web و Rocket",
    productPath: "/products/rust",
    dependencyFile: "Cargo.toml و Cargo.lock",
    buildCommand: "cargo build --release",
    runCommand: "./target/release/app",
    portNote: "نام باینری را با package پروژه هماهنگ و listener را روی 0.0.0.0:$PORT تنظیم کنید.",
    productionNotes: [
      "Cargo.lock را برای برنامه production در Git نگه دارید.",
      "برای کاهش image از build چندمرحله‌ای استفاده کنید.",
      "SIGTERM و connection drain را در shutdown مدیریت کنید.",
    ],
    primaryKeyword: "دیپلوی rust",
    secondaryKeywords: ["دیپلوی پروژه rust", "آموزش دیپلوی rust"],
    seoTitle: "دیپلوی rust؛ Axum، Actix و Rocket | گردو کلاد",
    seoDescription: "دیپلوی rust روی سکوی ابری گردو برای Axum، Actix Web و Rocket؛ از Git یا CLI با دستور ساخت و اجرا، متغیرها و دامنه. سرویس Rust خود را منتشر کنید.",
    h1Title: "دیپلوی rust روی گردو",
    keywords: "دیپلوی rust, دیپلوی پروژه rust, آموزش دیپلوی rust, هاست rust, کانتینر ابری",
    relatedLinks: [
      { label: "آموزش دیپلوی", to: "/guides" },
      { label: "کانتینر ابری", to: "/products/containers" },
      { label: "هاست rust", to: "/products/rust" },
    ],
  },
  {
    slug: "react",
    name: "React",
    frameworks: "React، Vite و SPA",
    productPath: "/products/react",
    dependencyFile: "package.json و lockfile",
    buildCommand: "npm ci && npm run build",
    runCommand: "npx serve -s dist -l $PORT",
    portNote: "متغیرهای Vite در زمان build خوانده می‌شوند و معمولاً پیشوند VITE_ دارند.",
    productionNotes: [
      "fallback مسیرهای SPA را به index.html فعال کنید.",
      "کلید محرمانه را داخل bundle مرورگر قرار ندهید.",
      "API را سرویس جداگانه مستقر و CORS را محدود کنید.",
    ],
    primaryKeyword: "دیپلوی react",
    secondaryKeywords: ["دیپلوی ری اکت", "دیپلوی پروژه react", "آپلود پروژه react روی هاست"],
    seoTitle: "دیپلوی react و Vite روی هاست ابری | گردو کلاد",
    seoDescription: "دیپلوی react و Vite روی سکوی ابری گردو از Git یا CLI؛ بیلد خودکار، متغیرها، دامنه و SSL برای پنل‌ها و سایت‌های SPA. پروژه‌ی React را منتشر کنید.",
    h1Title: "دیپلوی react و Vite روی گردو",
    keywords: "دیپلوی react, دیپلوی ری اکت, دیپلوی پروژه react, آپلود پروژه react روی هاست, هاست react, دیپلوی nextjs",
    relatedLinks: [
      { label: "هاست react", to: "/products/react" },
      { label: "آموزش دیپلوی", to: "/guides" },
      { label: "دیپلوی nextjs", to: "/guides/deploy-nextjs" },
    ],
  },
  {
    slug: "nextjs",
    name: "Next.js",
    frameworks: "SSR، App Router و API Routes",
    productPath: "/products/nextjs",
    dependencyFile: "package.json و lockfile",
    buildCommand: "npm ci && npm run build",
    runCommand: "npm start -- -H 0.0.0.0 -p $PORT",
    portNote: "متغیرهای NEXT_PUBLIC_ وارد کد مرورگر می‌شوند؛ secretها نباید این پیشوند را داشته باشند.",
    productionNotes: [
      "برای SSR از خروجی server استفاده کنید، نه export استاتیک.",
      "نسخه Node را در package.json ثابت کنید.",
      "کش و فایل‌های تولیدی پایدار را آگاهانه روی storage جدا قرار دهید.",
    ],
    primaryKeyword: "دیپلوی nextjs",
    secondaryKeywords: ["دیپلوی next js", "دیپلوی نکست", "آپلود پروژه next js روی هاست"],
    seoTitle: "دیپلوی nextjs با SSR و App Router | گردو کلاد",
    seoDescription: "دیپلوی nextjs روی سکوی ابری گردو با SSR، App Router و API Routes؛ از Git یا CLI با دستور ساخت و اجرا، متغیرها و دامنه. پروژه‌ی Next.js را منتشر کنید.",
    h1Title: "دیپلوی nextjs روی گردو",
    keywords: "دیپلوی nextjs, دیپلوی next js, دیپلوی نکست, آپلود پروژه next js روی هاست, هاست Next js, دیپلوی react",
    relatedLinks: [
      { label: "هاست Next js", to: "/products/nextjs" },
      { label: "آموزش دیپلوی", to: "/guides" },
      { label: "دیپلوی react", to: "/guides/deploy-react" },
    ],
  },
  {
    slug: "vue",
    name: "Vue و Nuxt",
    frameworks: "Vue SPA و Nuxt SSR",
    productPath: "/products/vue",
    dependencyFile: "package.json و lockfile",
    buildCommand: "npm ci && npm run build",
    runCommand: "node .output/server/index.mjs",
    portNote: "برای Nuxt، NITRO_HOST=0.0.0.0 و NITRO_PORT=$PORT را تنظیم کنید؛ Vue SPA را به‌صورت static سرو کنید.",
    productionNotes: [
      "بین خروجی SPA و Nuxt SSR پیش از deploy تصمیم بگیرید.",
      "متغیر عمومی و secret را در runtimeConfig جدا کنید.",
      "fallback مسیرهای SPA را برای Vue Router فعال کنید.",
    ],
    primaryKeyword: "دیپلوی vue",
    secondaryKeywords: ["دیپلوی nuxt", "آپلود پروژه vue روی هاست"],
    seoTitle: "دیپلوی vue و Nuxt روی هاست ابری | گردو کلاد",
    seoDescription: "دیپلوی vue و Nuxt روی سکوی ابری گردو برای Vue SPA و Nuxt SSR؛ از Git یا CLI با بیلد خودکار، متغیرها و دامنه. پروژه‌ی Vue را در چند دقیقه منتشر کنید.",
    h1Title: "دیپلوی vue و Nuxt روی گردو",
    keywords: "دیپلوی vue, دیپلوی nuxt, آپلود پروژه vue روی هاست, هاست vue",
    relatedLinks: [
      { label: "هاست vue", to: "/products/vue" },
      { label: "آموزش دیپلوی", to: "/guides" },
      { label: "سکوی ابری گردو", to: "/products/paas" },
    ],
  },
  {
    slug: "svelte",
    name: "Svelte و SvelteKit",
    frameworks: "Svelte SPA، SvelteKit SSR و static",
    productPath: "/products/svelte",
    dependencyFile: "package.json و lockfile",
    buildCommand: "npm ci && npm run build",
    runCommand: "node build",
    portNote: "برای SSR از adapter-node و متغیرهای HOST=0.0.0.0 و PORT استفاده کنید.",
    productionNotes: [
      "adapter را متناسب با SSR یا static انتخاب کنید.",
      "متغیرهای public و private را از ماژول درست $env بخوانید.",
      "برای SPA fallback مسیرهای سمت کاربر را فعال کنید.",
    ],
    primaryKeyword: "دیپلوی svelte",
    secondaryKeywords: ["دیپلوی sveltekit"],
    seoTitle: "دیپلوی svelte و SvelteKit روی ابر | گردو کلاد",
    seoDescription: "دیپلوی svelte و SvelteKit روی سکوی ابری گردو برای SPA، SSR و استاتیک؛ از Git یا CLI با دستور ساخت و اجرا، متغیرها و دامنه. پروژه را منتشر کنید.",
    h1Title: "دیپلوی svelte و SvelteKit روی گردو",
    keywords: "دیپلوی svelte, دیپلوی sveltekit, هاست svelte",
    relatedLinks: [
      { label: "هاست svelte", to: "/products/svelte" },
      { label: "آموزش دیپلوی", to: "/guides" },
      { label: "سکوی ابری گردو", to: "/products/paas" },
    ],
  },
  {
    slug: "astro",
    name: "Astro",
    frameworks: "Astro static و SSR",
    productPath: "/products/astro",
    dependencyFile: "package.json و lockfile",
    buildCommand: "npm ci && npm run build",
    runCommand: "node ./dist/server/entry.mjs",
    portNote: "برای SSR از adapter-node در حالت standalone استفاده کنید؛ خروجی static فقط به web server نیاز دارد.",
    productionNotes: [
      "output و adapter را در astro.config مشخص کنید.",
      "secretهای SSR را از import.meta.env بخوانید و عمومی نکنید.",
      "تصاویر و assetهای build را cache کنید.",
    ],
    primaryKeyword: "دیپلوی astro",
    secondaryKeywords: ["دیپلوی پروژه astro"],
    seoTitle: "دیپلوی astro استاتیک و SSR | گردو کلاد",
    seoDescription: "دیپلوی astro روی سکوی ابری گردو برای حالت استاتیک و SSR؛ از Git یا CLI با دستور ساخت و اجرا، متغیرها، دامنه و SSL. سایت Astro را منتشر کنید.",
    h1Title: "دیپلوی astro روی گردو",
    keywords: "دیپلوی astro, دیپلوی پروژه astro, هاست astro",
    relatedLinks: [
      { label: "هاست astro", to: "/products/astro" },
      { label: "آموزش دیپلوی", to: "/guides" },
      { label: "سکوی ابری گردو", to: "/products/paas" },
    ],
  },
  {
    slug: "static-sites",
    name: "سایت استاتیک",
    frameworks: "HTML/CSS/JS، Vite و Static Generators",
    productPath: "/products/static-sites",
    dependencyFile: "فایل‌های آماده یا package.json ابزار build",
    buildCommand: "npm ci && npm run build",
    runCommand: "npx serve -s dist -l $PORT",
    portNote: "پوشه خروجی ممکن است dist، build، out یا public باشد؛ آن را مطابق پروژه تنظیم کنید.",
    productionNotes: [
      "برای SPA fallback را فعال کنید.",
      "دارایی‌های hash‌شده را با cache طولانی سرو کنید.",
      "فرم و API نیازمند سرویس backend جدا هستند.",
    ],
    primaryKeyword: "دیپلوی سایت استاتیک",
    secondaryKeywords: ["آپلود سایت html", "آپلود سایت html روی هاست", "انتشار سایت html"],
    seoTitle: "دیپلوی سایت استاتیک HTML روی هاست | گردو کلاد",
    seoDescription: "دیپلوی سایت استاتیک HTML، CSS و JS یا خروجی Vite روی سکوی ابری گردو از Git یا CLI؛ با دامنه‌ی اختصاصی و SSL. سایت استاتیک خود را در چند دقیقه منتشر کنید.",
    h1Title: "دیپلوی سایت استاتیک روی گردو",
    keywords: "دیپلوی سایت استاتیک, آپلود سایت html, آپلود سایت html روی هاست, انتشار سایت html, هاست استاتیک, هاست رایگان پرسرعت",
    relatedLinks: [
      { label: "هاست استاتیک", to: "/products/static-sites" },
      { label: "هاست رایگان پرسرعت", to: "/free-host" },
      { label: "آموزش دیپلوی", to: "/guides" },
    ],
  },
];

export function findStackGuide(slug: string) {
  return STACK_GUIDES.find((guide) => guide.slug === slug);
}
